import { useState } from 'react';
import { Edit, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatAddressText } from '~/utils/addressFormat';
import type { LocationRowData } from './OrganizationLocations';

type Props = {
  location: LocationRowData;
  isAssigned: boolean;
  onToggleAssignment: () => Promise<void>;
  onEdit: () => void;
  onDelete: () => void;
};

export function LocationRow({
  location,
  isAssigned,
  onToggleAssignment,
  onEdit,
  onDelete,
}: Props) {
  const { t } = useTranslation('settings');
  const [isPending, setIsPending] = useState(false);

  async function handleAssignToggle() {
    if (isPending) return;
    setIsPending(true);
    try {
      await onToggleAssignment();
    } finally {
      setIsPending(false);
    }
  }

  return (
    <div
      data-testid="location-row"
      className="flex items-center justify-between gap-4 py-2 px-3 rounded-lg hover:bg-neut-50"
    >
      <div className="min-w-0">
        <div className="flex items-baseline gap-2">
          <span
            className="text-sm font-semibold text-neut-prim"
            data-testid="location-row-code"
          >
            {location.code}
          </span>
          <span className="text-xs text-neut-400">{location.name}</span>
        </div>
        <p
          className="text-xs text-neut-sec truncate"
          data-testid="location-row-address"
        >
          {formatAddressText(location.facility.address)}
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          type="button"
          disabled={isPending}
          onClick={handleAssignToggle}
          className={`px-2 py-0.5 rounded text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
            isAssigned
              ? 'border border-neut-300 text-neut-700 hover:bg-neut-50'
              : 'bg-prim text-white hover:bg-prim-700'
          }`}
        >
          {isAssigned
            ? t('settings.facilities.locations.unassign')
            : t('settings.facilities.locations.assign')}
        </button>
        <button
          type="button"
          aria-label={t('settings.facilities.locations.edit_aria')}
          onClick={onEdit}
          className="text-neut hover:text-neut-700 transition-colors"
        >
          <Edit size={14} />
        </button>
        <button
          type="button"
          aria-label={t('settings.facilities.locations.delete_aria')}
          onClick={onDelete}
          className="text-error-500 hover:text-error-700 transition-colors"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}
